import React from 'react';
import type { FC } from 'react';
import { match } from 'variant';
import { Box, Text, useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import { useARMClient } from '@wix/arm-client';
import { isInsideGitRepo, getGitLatestCommitHash } from '@wix/package-manager';
import { useProjectModel } from '../../model.js';
import { assertBuildOutputExists } from '../../files/assert-build-output-exists.js';
import { ActionStatus } from './ActionStatus.js';
import { readAppManifestFile } from '../../app-manifest/file.js';
import { useLoadBuildFiles } from './hooks/useLoadBuildFiles.js';
import { useUpdateManifest } from './hooks/useUpdateManifest.js';
import { PlatformLink } from '../PlatformLink/PlatformLink.js';
import { useUpdateDeploymentTopology } from '../../hooks/useUpdateDeploymentTopology.js';
import { useDeployFiles } from './hooks/useDeployFiles.js';

export const Preview: FC<{
  environment: DeploymentTopologyEnvironmentType;
  baseUrl?: string;
  label?: string;
  onPreviewCreated?: (args: { armTag: string; serverUrl: string }) => void;
}> = ({ environment, baseUrl, label, onPreviewCreated }) => {
  const { model } = useProjectModel();
  const armClient = useARMClient();
  const loadBuildFiles = useLoadBuildFiles();
  const deployFiles = useDeployFiles();
  const updateDeploymentTopology = useUpdateDeploymentTopology();
  const { updateManifest, updateManifestWithBackendWorker } =
    useUpdateManifest();

  const uploadApplicationAction = useAsyncCallback(
    async (_, baseUrl?: string) => {
      const buildFiles = await loadBuildFiles.execute();
      const { deploymentBaseUrl, deploymentId } =
        await deployFiles.execute(buildFiles);

      return {
        deploymentUrl: baseUrl ?? deploymentBaseUrl,
        deploymentId,
      };
    }
  );

  const createPreviewTagAction = useAsyncCallback(
    async (_, serverUrl: string, deploymentId: string) => {
      const manifest = await readAppManifestFile(model.projectFolder);

      const gitCommitHash = (await isInsideGitRepo(model.projectFolder))
        ? await getGitLatestCommitHash(model.projectFolder)
        : undefined;

      const updatedManifest = updateManifest(
        updateManifestWithBackendWorker(
          manifest,
          deploymentId,
          serverUrl,
          label,
          gitCommitHash
        ),
        serverUrl
      );

      return armClient.createLooseAppRelease(
        updatedManifest,
        model.config.appId
      );
    }
  );

  const { status: createPreviewStatus } = useAsync(async () => {
    await assertBuildOutputExists(model.projectFolder);

    const { deploymentUrl: depoymentUrl, deploymentId } =
      await uploadApplicationAction.execute(baseUrl);

    const armTag = await createPreviewTagAction.execute(
      depoymentUrl,
      deploymentId
    );

    await updateDeploymentTopology.execute({
      serverUrl: depoymentUrl,
      armTag,
      environment,
    });

    onPreviewCreated?.({ armTag, serverUrl: depoymentUrl });

    return { armTag, depoymentUrl };
  }, []);

  return (
    <>
      <ActionStatus
        status={uploadApplicationAction.status}
        loadingText={'Uploading your application files...'}
        successText={'Uploaded successfully!'}
      />
      <ActionStatus
        status={createPreviewTagAction.status}
        loadingText={'Creating a preview...'}
        successText={'Preview created successfully!'}
      />
      <ActionStatus
        status={updateDeploymentTopology.status}
        loadingText={'Updating deployment topology manifest...'}
        successText={'Deployment topology manifest updated successfully!'}
      />
      {match(createPreviewStatus, {
        Error: () => null,
        Loading: () => null,
        Success: ({ result: { armTag, depoymentUrl } }) => (
          <Box rowGap={1} marginY={1} flexDirection="column">
            <Text>Open the preview on:</Text>
            <Box paddingLeft={2} flexDirection="column">
              <PlatformLink url={depoymentUrl} label={'Site'} />
              <PlatformLink
                url={`https://manage.wix.com/dashboard/${model.config.siteId}?apps-override=${armTag}`}
                label={'Dashboard'}
              />
            </Box>
          </Box>
        ),
      })}
    </>
  );
};
